export const getExperimentArm = (experiments, experimentName) => {
    return (experiments === null || experiments === void 0 ? void 0 : experiments[experimentName]) || 'C';
};
/** Frontend key for the multi-ASIN landing page weblab. */
const MALP_WEBLAB = 'WEBLAB_PAINTER_MULTI_ASIN_LANDING_PAGE';
/**
 * Resolves whether the multi-ASIN landing page is available for this request,
 * and if not, why. Pure: no React, no browser state.
 *
 * Fails closed — returns `isAvailable: true` only on the new route, with the
 * weblab arm resolving to exactly `T1`. Every other input (any other arm, an
 * absent weblab entry, or an unresolved `experiments` object) is unavailable,
 * so an unregistered or dialed-off weblab — including the state before the
 * server-side registration lands, where the key is simply absent — renders
 * nothing. Marketplace gating (including EU) is left to the weblab allocation.
 *
 * The raw `experiments[MALP_WEBLAB]` value is read *before* `getExperimentArm`,
 * because `getExperimentArm` collapses an absent entry to `'C'`, which would
 * make `weblab-absent` and `weblab-control` indistinguishable on the RUM event.
 *
 * @param isMALP - Whether the current route is the multi-ASIN landing page
 * @param experiments - The `experiments` map from `useAppInit`'s data (undefined until it resolves)
 * @param marketplaceId - The resolved marketplace (from `getMarketplace`)
 */
export const resolveMultiAsinAvailability = (isMALP, experiments, marketplaceId) => {
    const arm = experiments === null || experiments === void 0 ? void 0 : experiments[MALP_WEBLAB];
    const unavailable = (reason) => ({
        isAvailable: false,
        arm,
        marketplaceId,
        reason,
    });
    // Off the route the page never mounts, so no reason is observable.
    if (!isMALP) {
        return unavailable(null);
    }
    // appInit has not resolved yet.
    if (experiments === undefined) {
        return unavailable('app-init-unresolved');
    }
    // Weblab key absent from the map (e.g. server-side registration not yet live).
    if (arm === undefined) {
        return unavailable('weblab-absent');
    }
    if (getExperimentArm(experiments, MALP_WEBLAB) === 'T1') {
        return { isAvailable: true, arm: 'T1', marketplaceId };
    }
    // Present but not T1: exactly 'C' is control; anything else (T2, T3, or an
    // out-of-set value) is an arm that still does not render the page.
    return unavailable(arm === 'C' ? 'weblab-control' : 'weblab-unknown-arm');
};
